import {
  AfterContentInit,
  Component,
  ContentChildren,
  input,
  OnChanges,
  OnDestroy,
  output,
  QueryList,
  SimpleChanges,
} from '@angular/core';
import { NaturalLanguageEditorKeyphraseAbstractDirective } from './natural-language-editor-keyphrase/natural-language-editor-keyphrase.abstract.directive';
import { Subscription } from 'rxjs';

export interface NaturalLanguageEditorEntityValidation<T> {
  key: string;
  validator: (entity: T) => boolean;
  message: string;
}

@Component({
  selector: 'ziro-natural-language-editor',
  templateUrl: 'natural-language-editor.component.html',
  styleUrls: ['natural-language-editor.component.scss'],
  standalone: false,
})
export class NaturalLanguageEditorComponent<T> implements AfterContentInit, OnChanges, OnDestroy {
  readonly entity = input<T>();
  readonly disabled = input<boolean>();
  readonly disabledTooltip = input<string>();
  readonly dataAutomation = input<string>();
  readonly entityUpdate = output<T>();
  readonly validationUpdate = output<boolean>();
  entityValidators = input<NaturalLanguageEditorEntityValidation<T>[]>([]);

  @ContentChildren(NaturalLanguageEditorKeyphraseAbstractDirective, { descendants: true })
  keyphrases: QueryList<NaturalLanguageEditorKeyphraseAbstractDirective<T[keyof T]>>;

  feedback = '';
  externalFeedback = '';

  private _subscriptions = new Subscription();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.entity) {
      this.keyphrases?.forEach((keyphrase) => {
        const previousValue = changes.entity.previousValue && changes.entity.previousValue[keyphrase.phraseId()];
        const currentValue = changes.entity.currentValue[keyphrase.phraseId()];
        if (currentValue !== previousValue) {
          keyphrase.onUpdate(currentValue);
        }
      });
      this._recomputeEntityValidationFeedback();
      this.validationUpdate.emit(!!(this.feedback || this.externalFeedback));
    }

    if (changes.entityValidators) {
      this._recomputeEntityValidationFeedback();
      this.validationUpdate.emit(!!(this.feedback || this.externalFeedback));
    }
  }

  ngAfterContentInit(): void {
    this.keyphrases.forEach((keyphrase) => {
      keyphrase.onUpdate(this.entity()[keyphrase.phraseId() as keyof T]);
      const sub = keyphrase.update$.subscribe((update) => {
        if (update.feedback != null) {
          this.feedback = update.feedback;
          this._recomputeEntityValidationFeedback();
          this.validationUpdate.emit(!!(update.feedback || this.externalFeedback));
        }
        this.entityUpdate.emit({ ...this.entity(), [update.phraseId]: update.value });
      });
      this._subscriptions.add(sub);
    });
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  private _recomputeEntityValidationFeedback(): void {
    this.externalFeedback = '';
    const entity = this.entity();
    if (!entity) {
      return;
    }

    for (const rule of this.entityValidators() ?? []) {
      if (!rule.validator(entity)) {
        this.externalFeedback = rule.message;
        return;
      }
    }
  }
}
